import { useState } from 'react';
import { Box, Typography, IconButton, TextField, Button, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

import proposalsServices from '../../services/proposalsServices';

const fontFamily = '"Inter", sans-serif';

const ProposalViewEquipmentOverview = ({ description, proposalId, onRefresh, editable }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleStartEdit = () => {
    setEditValue(description || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await proposalsServices.update(proposalId, {
        equipmentOverview: editValue.trim() || null,
      });
      if (response.status === 200) {
        toast.success('Equipment Overview updated');
        setIsEditing(false);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save Equipment Overview');
    } finally {
      setSaving(false);
    }
  };

  // No description — hide entirely (same as before the edit feature)
  if (!description) return null;

  return (
    <Box sx={{ mb: 4, px: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
        <Box
          sx={{
            backgroundColor: '#0d3c61',
            color: '#fff',
            px: 2,
            py: 0.75,
            display: 'inline-block',
          }}
        >
          <Typography
            sx={{
              fontFamily,
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Equipment Overview
          </Typography>
        </Box>

        {/* Edit button — hidden on print */}
        {editable && !isEditing && (
          <Tooltip title="Edit Equipment Overview">
            <IconButton
              size="small"
              onClick={handleStartEdit}
              className="no-print"
              sx={{ ml: 1, color: '#0d3c61', '@media print': { display: 'none !important' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Content */}
      {isEditing ? (
        <Box className="no-print" sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, borderTopLeftRadius: 0, p: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            variant="outlined"
            placeholder="Describe the equipment being serviced and the work to be performed..."
            size="small"
            autoFocus
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ backgroundColor: '#0d3c61', '&:hover': { backgroundColor: '#0a2d4a' } }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, borderTopLeftRadius: 0, p: 2 }}>
          <Typography
            sx={{
              fontFamily,
              fontSize: 13,
              color: '#333',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
            }}
          >
            {description}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProposalViewEquipmentOverview;
